/**
 * Alert Component
 *
 * Displays contextual feedback messages with optional actions.
 *
 * GAP-010: Enhanced with action support for actionable error messages
 */

import type { ReactNode } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils/cn';

const alertVariants = cva(
  'flex items-start gap-3 rounded-lg border p-4',
  {
    variants: {
      variant: {
        default: 'border-mv-gray-200 bg-mv-gray-50 text-mv-gray-700',
        info: 'border-mv-blue-200 bg-mv-blue-50 text-mv-blue-700',
        success: 'border-green-200 bg-green-50 text-green-700',
        warning: 'border-amber-200 bg-amber-50 text-amber-700',
        error: 'border-red-200 bg-red-50 text-red-700',
        danger: 'border-red-200 bg-red-50 text-red-700',
      },
      size: {
        sm: 'p-3 text-sm',
        md: 'p-4',
        lg: 'p-5 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

const iconMap = {
  default: InformationCircleIcon,
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  error: XCircleIcon,
  danger: XCircleIcon,
};

/**
 * GAP-010: Action button configuration for actionable alerts
 */
export interface AlertAction {
  /** Action label */
  label: string;
  /** Action handler */
  onClick: () => void;
  /** Whether action is loading */
  loading?: boolean;
  /** Icon to show (defaults to ArrowRightIcon for primary, ArrowPathIcon for retry) */
  icon?: ReactNode;
  /** Action variant */
  variant?: 'primary' | 'secondary' | 'retry';
}

export interface AlertProps extends VariantProps<typeof alertVariants> {
  /** Alert title */
  title?: string;
  /** Alert content */
  children?: ReactNode;
  /** Custom icon */
  icon?: ReactNode;
  /** Additional className */
  className?: string;
  /** Show/hide the default icon */
  showIcon?: boolean;
  /** Callback when dismiss button is clicked */
  onDismiss?: () => void;
  /** GAP-010: Primary action button */
  action?: AlertAction;
  /** GAP-010: Secondary actions */
  secondaryActions?: AlertAction[];
}

export function Alert({
  variant = 'default',
  size = 'md',
  title,
  children,
  icon,
  className,
  showIcon = true,
  onDismiss,
  action,
  secondaryActions,
}: AlertProps) {
  const IconComponent = iconMap[variant ?? 'default'];

  // GAP-010: Get action button styles based on variant
  const getActionButtonClass = (actionVariant?: 'primary' | 'secondary' | 'retry') => {
    const base = 'inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 min-h-[36px] md:min-h-[32px]';

    if (actionVariant === 'retry') {
      return cn(base, 'bg-red-100 text-red-700 hover:bg-red-200 focus:ring-red-500');
    }
    if (actionVariant === 'secondary') {
      return cn(base, 'bg-white/50 text-inherit hover:bg-white/80 focus:ring-current');
    }
    // Primary - depends on alert variant
    if (variant === 'error' || variant === 'danger') {
      return cn(base, 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500');
    }
    if (variant === 'warning') {
      return cn(base, 'bg-amber-600 text-white hover:bg-amber-700 focus:ring-amber-500');
    }
    if (variant === 'success') {
      return cn(base, 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500');
    }
    return cn(base, 'bg-mv-blue-600 text-white hover:bg-mv-blue-700 focus:ring-mv-blue-500');
  };

  const getActionIcon = (alertAction: AlertAction) => {
    if (alertAction.icon) return alertAction.icon;
    if (alertAction.variant === 'retry') return <ArrowPathIcon className="h-4 w-4" />;
    return <ArrowRightIcon className="h-4 w-4" />;
  };

  const allActions = action ? [action, ...(secondaryActions || [])] : secondaryActions || [];

  return (
    <div className={cn(alertVariants({ variant, size }), className)} role="alert">
      {showIcon && (
        <div className="shrink-0">
          {icon || <IconComponent className="h-5 w-5" aria-hidden="true" />}
        </div>
      )}
      <div className="flex-1 min-w-0">
        {title && <h3 className="font-medium">{title}</h3>}
        {children && <div className={cn(title && 'mt-1', 'text-sm')}>{children}</div>}
        {/* GAP-010: Action buttons */}
        {allActions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {allActions.map((alertAction, idx) => (
              <button
                key={idx}
                type="button"
                onClick={alertAction.onClick}
                disabled={alertAction.loading}
                className={getActionButtonClass(alertAction.variant || (idx === 0 ? 'primary' : 'secondary'))}
              >
                {alertAction.loading ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  getActionIcon(alertAction)
                )}
                {alertAction.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-md p-1 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2"
          aria-label="Sluiten"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/**
 * GAP-010: ErrorAlert - Specialized alert for actionable error messages
 *
 * Provides user-friendly error messages with suggested actions based on error type.
 */
export interface ErrorAlertProps {
  /** Error object or message */
  error: Error | string | null;
  /** Optional title override */
  title?: string;
  /** Retry handler - if provided, shows retry button */
  onRetry?: () => void;
  /** Whether retry is in progress */
  retrying?: boolean;
  /** Help/support link */
  helpUrl?: string;
  /** Additional className */
  className?: string;
  /** Dismiss handler */
  onDismiss?: () => void;
}

/**
 * Map common error patterns to user-friendly messages with actions
 */
function getErrorDetails(error: Error | string): { message: string; suggestion?: string; isRetryable: boolean } {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const lowerMessage = errorMessage.toLowerCase();

  // Network errors
  if (lowerMessage.includes('network') || lowerMessage.includes('fetch') || lowerMessage.includes('connection')) {
    return {
      message: 'Kan geen verbinding maken met de server.',
      suggestion: 'Controleer uw internetverbinding en probeer het opnieuw.',
      isRetryable: true,
    };
  }

  // Timeout errors
  if (lowerMessage.includes('timeout') || lowerMessage.includes('timed out')) {
    return {
      message: 'De server reageert niet.',
      suggestion: 'De server is mogelijk overbelast. Probeer het over enkele minuten opnieuw.',
      isRetryable: true,
    };
  }

  // Auth errors
  if (lowerMessage.includes('unauthorized') || lowerMessage.includes('401') || lowerMessage.includes('session')) {
    return {
      message: 'Uw sessie is verlopen.',
      suggestion: 'Log opnieuw in om door te gaan.',
      isRetryable: false,
    };
  }

  // Forbidden
  if (lowerMessage.includes('forbidden') || lowerMessage.includes('403') || lowerMessage.includes('permission')) {
    return {
      message: 'U heeft geen toegang tot deze actie.',
      suggestion: 'Neem contact op met uw beheerder als u denkt dat dit een fout is.',
      isRetryable: false,
    };
  }

  // Not found
  if (lowerMessage.includes('not found') || lowerMessage.includes('404')) {
    return {
      message: 'De gevraagde gegevens zijn niet gevonden.',
      suggestion: 'Het item is mogelijk verwijderd of verplaatst.',
      isRetryable: false,
    };
  }

  // Validation errors
  if (lowerMessage.includes('validation') || lowerMessage.includes('invalid') || lowerMessage.includes('400')) {
    return {
      message: 'De ingevoerde gegevens zijn ongeldig.',
      suggestion: 'Controleer de velden en probeer het opnieuw.',
      isRetryable: false,
    };
  }

  // Server errors (5xx)
  if (lowerMessage.includes('500') || lowerMessage.includes('502') || lowerMessage.includes('503') || lowerMessage.includes('server error')) {
    return {
      message: 'Er is een serverfout opgetreden.',
      suggestion: 'Ons team is op de hoogte. Probeer het later opnieuw.',
      isRetryable: true,
    };
  }

  // File/upload errors
  if (lowerMessage.includes('upload') || lowerMessage.includes('file')) {
    return {
      message: 'Het bestand kon niet worden verwerkt.',
      suggestion: 'Controleer of het bestand het juiste formaat heeft en niet te groot is.',
      isRetryable: true,
    };
  }

  // Default - still provide helpful context
  return {
    message: errorMessage || 'Er is een onverwachte fout opgetreden.',
    suggestion: 'Probeer het opnieuw of neem contact op met ondersteuning.',
    isRetryable: true,
  };
}

export function ErrorAlert({
  error,
  title,
  onRetry,
  retrying,
  helpUrl,
  className,
  onDismiss,
}: ErrorAlertProps) {
  if (!error) return null;

  const { message, suggestion, isRetryable } = getErrorDetails(error);

  const actions: AlertAction[] = [];

  // Add retry button if handler provided and error is retryable
  if (onRetry && isRetryable) {
    actions.push({
      label: 'Opnieuw proberen',
      onClick: onRetry,
      loading: retrying,
      variant: 'retry',
    });
  }

  // Add help link if provided
  if (helpUrl) {
    actions.push({
      label: 'Hulp nodig?',
      onClick: () => window.open(helpUrl, '_blank'),
      variant: 'secondary',
    });
  }

  return (
    <Alert
      variant="error"
      title={title || 'Er ging iets mis'}
      action={actions[0]}
      secondaryActions={actions.slice(1)}
      onDismiss={onDismiss}
      className={className}
    >
      <p>{message}</p>
      {suggestion && <p className="mt-1 text-red-600/80">{suggestion}</p>}
    </Alert>
  );
}
